import { useLenderOfferAmendment } from "../../components/AmendLenderOfferButton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Modal,
  Select,
  Tag,
  Timeline,
  Tooltip,
  message,
  Input,
  InputNumber,
  Switch,
  Slider,
} from "antd";
import Cookies from "js-cookie";
import { CircularProgress, useMediaQuery } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import Box from "@mui/material/Box";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const STATUS_COPY = {
  open: { label: "Open", color: "blue" },
  reviewing: { label: "Reviewing", color: "geekblue" },
  funded: { label: "Funded", color: "green" },
  closed: { label: "Closed", color: "default" },
};

const OFFER_STATUS_COPY = {
  pending_borrower_action: "Awaiting borrower response",
  borrower_renegotiation: "Borrower requested changes",
  borrower_rejected: "Borrower rejected",
  awaiting_lender_documents: "Upload documents",
  pending_borrower_signature: "Borrower reviewing documents",
  pending_lender_confirmation: "Review signed documents",
  revision_requested: "Awaiting borrower updates",
  pending_disbursement: "Pending disbursement",
  ongoing: "Ongoing",
  funded: "Funded",
};

const HISTORY_LABELS = {
  offer_created: "Offer created",
  offer_updated: "Offer updated",
  accepted_offer: "Borrower accepted",
  rejected_offer: "Borrower rejected",
  requested_renegotiation: "Borrower requested renegotiation",
  uploaded_initial_documents: "Uploaded documents for borrower",
  uploaded_signed_documents: "Borrower uploaded signed documents",
  reuploaded_documents: "Borrower re-uploaded documents",
  documents_confirmed: "Documents confirmed",
  revision_requested: "Revision requested",
  uploaded_document: "Document uploaded",
  borrower_confirmed_documents: "Borrower confirmed documents ready",
};

const HISTORY_COLOR = {
  lender: "blue",
  borrower: "green",
  system: "gray",
};

const PRIMARY_COLOR = "rgb(10, 61, 98)";
const PRIMARY_BUTTON_STYLE = {
  background: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  color: "#fff",
};
const GLASS_CARD_BACKGROUND =
  "linear-gradient(135deg, rgba(10,61,98,0.12), rgba(255,255,255,0.95))";
const HIGHLIGHT_CARD_BACKGROUND =
  "linear-gradient(135deg, rgba(46,204,113,0.2), rgba(255,255,255,0.95))";

const YEAR_UNDATED = "undated";

const getYearKey = (rawDate) => {
  if (!rawDate) {
    return YEAR_UNDATED;
  }
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) {
    return YEAR_UNDATED;
  }
  return String(date.getFullYear());
};

const getYearLabel = (yearKey) =>
  yearKey === YEAR_UNDATED ? "No due date yet" : `Year ${yearKey}`;

const groupRepaymentsByYear = (rows = [], getDate = (row) => row?.due_date) => {
  const groups = new Map();
  const order = [];

  rows.forEach((row, index) => {
    const yearKey = getYearKey(getDate(row));
    if (!groups.has(yearKey)) {
      groups.set(yearKey, []);
      order.push(yearKey);
    }
    groups.get(yearKey).push({ row, index });
  });

  return order.map((year) => ({
    year,
    label: getYearLabel(year),
    items: groups.get(year),
  }));
};

const isSameYearMap = (first = {}, second = {}) => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) {
    return false;
  }
  return firstKeys.every((key) => second[key] === first[key]);
};

const MODAL_BODY_STYLE = {
  padding: 0,
  background: "transparent",
};

const GLASS_MODAL_PANEL_STYLE = {
  background: GLASS_CARD_BACKGROUND,
  borderRadius: 22,
  border: "1px solid rgba(10,61,98,0.12)",
  boxShadow: "0 36px 60px rgba(10,61,98,0.16)",
  backdropFilter: "blur(12px)",
  color: PRIMARY_COLOR,
  position: "relative",

};

const MODAL_MASK_STYLE = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(10,61,98,0.3)",
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    minimumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${numeric}% p.a.`;
};

const formatMonths = (value) => {
  if (value === undefined || value === null) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${numeric} month${numeric === 1 ? "" : "s"}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PersonalLoanMarketplace = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    mineOnly: false,
    hideRejected: true,
    status: "all",
    tenor: "all",
    amountRange: [1000, 100000],
  });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [offerModal, setOfferModal] = useState({
    open: false,
    loan: null,
    offer: null,
  });
  const [offerForm, setOfferForm] = useState({
    amount: "",
    rate_pa: "",
    tenor_years: "",
  });
  const [offerBusy, setOfferBusy] = useState(false);
  const [rejectionModal, setRejectionModal] = useState({
    open: false,
    loan: null,
  });
  const [rejectionOptions, setRejectionOptions] = useState([]);
  const [rejectionForm, setRejectionForm] = useState({
    reason: undefined,
    notes: "",
  });
  const [rejectionBusy, setRejectionBusy] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [disbursementDate, setDisbursementDate] = useState("");
  const [scheduleDraft, setScheduleDraft] = useState([]);
  const [expandedDraftYears, setExpandedDraftYears] = useState({});
  const [reviewMessage, setReviewMessage] = useState("");
  const fileInputRef = useRef({});

  const fetchMarketplace = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/marketplace",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Unable to load marketplace");
      const json = await response.json();
      setLoans(json.data || []);
    } catch (error) {
      console.error(error);
      message.error("Unable to load personal loan marketplace.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectionOptions = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/options/rejection-reasons",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch reasons");
      const json = await response.json();
      setRejectionOptions(json.options || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchMarketplace();
    fetchRejectionOptions();
  }, []);

  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  const filteredLoans = useMemo(() => {
    return loans.filter((loan) => {
      if (filters.mineOnly && !loan.my_offer) return false;
      if (filters.hideRejected && loan.rejected_by_me && !loan.my_offer)
        return false;

      if (filters.status !== "all" && loan.status !== filters.status) {
        return false;
      }

      const amount = Number(loan.amount || 0);
      if (amount < filters.amountRange[0] || amount > filters.amountRange[1]) {
        return false;
      }

      if (filters.tenor !== "all") {
        const months = Number(loan.desired_tenor_months || 0);
        if (filters.tenor === "short" && months > 12) return false;
        if (filters.tenor === "mid" && (months < 13 || months > 36))
          return false;
        if (filters.tenor === "long" && months <= 36) return false;
      }

      return true;
    });
  }, [loans, filters]);

  const resetFilters = () => {
    setFilters({
      mineOnly: false,
      hideRejected: true,
      status: "all",
      tenor: "all",
      amountRange: [1000, 100000],
    });
  };

  const currentDraftKey = offerModal.offer
    ? `offer-${offerModal.offer.id}`
    : "offer-draft";

  const groupedDraftSchedules = useMemo(
    () => groupRepaymentsByYear(scheduleDraft, (row) => row.due_date),
    [scheduleDraft]
  );

  useEffect(() => {
    if (!groupedDraftSchedules.length) {
      setExpandedDraftYears((prev) => {
        if (!prev[currentDraftKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[currentDraftKey];
        return next;
      });
      return;
    }

    setExpandedDraftYears((prev) => {
      const existing = prev[currentDraftKey] || {};
      const nextMap = {};
      groupedDraftSchedules.forEach(({ year }) => {
        nextMap[year] = existing[year] !== false;
      });

      if (isSameYearMap(existing, nextMap)) {
        return prev;
      }

      return {
        ...prev,
        [currentDraftKey]: nextMap,
      };
    });
  }, [groupedDraftSchedules, currentDraftKey]);

  const toggleDraftYear = (yearKey) => {
    setExpandedDraftYears((prev) => {
      const existing = prev[currentDraftKey] || {};
      const isOpen = existing[yearKey] !== false;
      return {
        ...prev,
        [currentDraftKey]: {
          ...existing,
          [yearKey]: !isOpen,
        },
      };
    });
  };

  const openOffer = (loan, offer = null) => {
    const data = offer?.offer_data || {};
    setOfferForm({
      amount: data.amount ?? loan.amount ?? "",
      rate_pa: data.rate_pa ?? loan.desired_rate_pa ?? "",
      tenor_years:
        data.tenor_years ??
        (loan.desired_tenor_months ? loan.desired_tenor_months / 12 : ""),
    });
    setDisbursementDate(offer?.disbursement_date || "");
    setScheduleDraft(
      (offer?.repayment_schedules || []).map((row) => ({
        id: row.id,
        due_date: row.due_date,
        amount_due: row.amount_due,
        principal_amount: row.principal_amount,
        interest_amount: row.interest_amount,
        fee_amount: row.fee_amount,
        loan_balance: row.loan_balance,
        notes: row.notes,
        status: row.status,
      }))
    );
    setReviewMessage(offer?.lender_notes || "");
    setOfferModal({ open: true, loan, offer });
  };

  const { renderAmendOfferButton, amendmentModal } = useLenderOfferAmendment();
  const closeOfferModal = () => {
    setOfferModal({ open: false, loan: null, offer: null });
    setOfferForm({ amount: "", rate_pa: "", tenor_years: "" });
    setOfferBusy(false);
    setReviewBusy(false);
  };

  const submitOffer = async () => {
    if (!offerModal.loan) return;

    const payload = {
      id: offerModal.offer?.id,
      personal_loan_request_id: offerModal.loan.id,
      offer_data: {
        amount: Number(offerForm.amount),
        rate_pa: Number(offerForm.rate_pa),
        tenor_years: Number(offerForm.tenor_years),
      },
      lender_message: reviewMessage || null,
    };

    if (!payload.offer_data.amount || payload.offer_data.amount <= 0) {
      message.warning("Please provide an offer amount.");
      return;
    }

    if (
      !payload.offer_data.tenor_years ||
      payload.offer_data.tenor_years <= 0
    ) {
      message.warning("Please provide a tenor in years.");
      return;
    }

    try {
      setOfferBusy(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/personalLoans/offers/upsert",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await response.json();
      if (![200, 201].includes(response.status)) {
        throw new Error(json?.message || "Unable to submit offer");
      }

      message.success(
        response.status === 201 ? "Offer submitted." : "Offer updated."
      );
      closeOfferModal();
      fetchMarketplace();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to submit offer.");
    } finally {
      setOfferBusy(false);
    }
  };

  const openRejection = (loan) => {
    setRejectionForm({ reason: undefined, notes: "" });
    setRejectionModal({ open: true, loan });
  };

  const submitRejection = async () => {
    if (!rejectionModal.loan) return;

    try {
      setRejectionBusy(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/${rejectionModal.loan.id}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify(rejectionForm),
        }
      );

      const json = await response.json();
      if (!response.ok)
        throw new Error(json?.message || "Unable to record rejection");

      message.success("Your response has been recorded.");
      setRejectionModal({ open: false, loan: null });
      fetchMarketplace();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to record rejection.");
    } finally {
      setRejectionBusy(false);
    }
  };

  const triggerFileDialog = (offerId) => {
    fileInputRef.current[offerId]?.click();
  };

  const handleFileChange = async (offer, event, stage = "initial") => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    let successCount = 0;

    try {
      setDocumentBusy(offer.id);
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "personal_loan");
        formData.append("file_id", offer.id);

        const uploadResponse = await fetch(
          "https://www.smart-lend.com/api/file/uploadUniversalFile",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: formData,
          }
        );

        const uploadJson = await uploadResponse.json();
        if (uploadResponse.status !== 200 || !uploadJson?.document_id) {
          throw new Error(uploadJson?.message || "Unable to upload file");
        }

        const attachResponse = await fetch(
          `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/documents`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
            body: JSON.stringify({
              document_id: uploadJson.document_id,
              stage,
            }),
          }
        );

        const attachJson = await attachResponse.json();
        if (!attachResponse.ok) {
          throw new Error(attachJson?.message || "Unable to register document");
        }

        successCount += 1;
      }

      if (successCount > 0) {
        message.success(
          successCount === 1
            ? "Document uploaded."
            : `${successCount} documents uploaded.`
        );
        fetchMarketplace();
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload document.");
    } finally {
      setDocumentBusy(null);
    }
  };

  const buildReviewPayload = () =>
    scheduleDraft.map((row) => ({
      id: row.id,
      due_date: row.due_date,
      amount_due: row.amount_due ? Number(row.amount_due) : 0,
      principal_amount: row.principal_amount
        ? Number(row.principal_amount)
        : null,
      interest_amount: row.interest_amount ? Number(row.interest_amount) : null,
      fee_amount: row.fee_amount ? Number(row.fee_amount) : null,
      loan_balance: row.loan_balance ? Number(row.loan_balance) : null,
      notes: row.notes || null,
    }));

  const handleReviewDecision = async (offer, decision) => {
    try {
      if (decision === "confirm") {
        if (!disbursementDate) {
          message.warning("Please set a disbursement date.");
          return;
        }
        if (!scheduleDraft.length) {
          message.warning("Please provide at least one repayment entry.");
          return;
        }
        const invalid = scheduleDraft.some(
          (row) => !row.due_date || !row.amount_due
        );
        if (invalid) {
          message.warning(
            "Each repayment entry needs a due date and total amount."
          );
          return;
        }
      }

      setReviewBusy(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/personalLoans/offers/${offer.id}/review-documents`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            decision,
            message: reviewMessage || null,
            disbursement_date:
              decision === "confirm" ? disbursementDate : undefined,
            repayments:
              decision === "confirm" ? buildReviewPayload() : undefined,
          }),
        }
      );

      const json = await response.json();
      if (!response.ok)
        throw new Error(json?.message || "Unable to update offer");

      message.success(
        decision === "confirm"
          ? "Documents confirmed."
          : "Revision request sent to borrower."
      );
      setOfferModal({ open: false, loan: null, offer: null });
      fetchMarketplace();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to update offer.");
    } finally {
      setReviewBusy(false);
    }
  };

  const updateScheduleRow = (index, field, value) => {
    setScheduleDraft((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const addScheduleRow = () => {
    setScheduleDraft((prev) => [
      ...prev,
      {
        id: null,
        due_date: "",
        amount_due: "",
        principal_amount: "",
        interest_amount: "",
        fee_amount: "",
        loan_balance: "",
        notes: "",
        status: "pending",
      },
    ]);
  };

  const removeScheduleRow = (index) => {
    setScheduleDraft((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const filtersPanel = (
    <div
      style={{
        width: "100%",
        maxWidth: isMobile ? "100%" : 300,
        background:
          "linear-gradient(180deg, rgba(10,61,98,0.08) 0%, rgba(255,255,255,0.92) 100%)",
        borderRadius: 18,
        padding: isMobile ? 18 : 22,
        boxShadow: "0px 18px 34px rgba(10,61,98,0.08)",
        border: "1px solid rgba(10,61,98,0.08)",
        position: isMobile ? "relative" : "sticky",
        top: isMobile ? "auto" : 90,
        boxSizing: "border-box",
        marginTop: isMobile ? 12 : 0,
        zIndex: 2,
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color: PRIMARY_COLOR }}>
        Filter applications
      </div>
      <div style={{ fontSize: 12, color: "rgba(10,61,98,0.65)", marginTop: 4 }}>
        Fine-tune the marketplace to surface the right borrowers.
      </div>

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>My offers only</div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Focus on borrowers you've already engaged.
            </div>
          </div>
          <Switch
            checked={filters.mineOnly}
            onChange={(checked) =>
              setFilters((prev) => ({ ...prev, mineOnly: checked }))
            }
          />
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Hide rejected</div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Keep the list focused on live opportunities.
            </div>
          </div>
          <Switch
            checked={filters.hideRejected}
            onChange={(checked) =>
              setFilters((prev) => ({ ...prev, hideRejected: checked }))
            }
          />
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Application status
        </div>
        <Select
          style={{ width: "100%", marginTop: 6 }}
          value={filters.status}
          onChange={(value) =>
            setFilters((prev) => ({ ...prev, status: value }))
          }
          options={[
            { value: "all", label: "All statuses" },
            { value: "open", label: "Open" },
            { value: "reviewing", label: "Reviewing" },
            { value: "funded", label: "Funded" },
          ]}
        />
      </div>

      <div style={{ marginTop: 22 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Preferred tenor
        </div>
        <Select
          style={{ width: "100%", marginTop: 6 }}
          value={filters.tenor}
          onChange={(value) =>
            setFilters((prev) => ({ ...prev, tenor: value }))
          }
          options={[
            { value: "all", label: "All tenors" },
            { value: "short", label: "Up to 12 months" },
            { value: "mid", label: "13 - 36 months" },
            { value: "long", label: "37+ months" },
          ]}
        />
      </div>

      <div style={{ marginTop: 22 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Loan amount
        </div>
        <div
          style={{ fontSize: 11, color: "rgba(10,61,98,0.6)", marginTop: 4 }}
        >
          S${filters.amountRange[0].toLocaleString()} – S$
          {filters.amountRange[1].toLocaleString()}
        </div>
        <Slider
          style={{ marginTop: 12 }}
          range
          min={1000}
          max={100000}
          step={1000}
          value={filters.amountRange}
          onChange={(value) =>
            setFilters((prev) => ({ ...prev, amountRange: value }))
          }
        />
      </div>

      <Button
        type="link"
        style={{ padding: 0, marginTop: 12, color: PRIMARY_COLOR }}
        onClick={resetFilters}
      >
        Reset filters
      </Button>
      <Button
        block
        style={{ marginTop: 10, ...PRIMARY_BUTTON_STYLE }}
        onClick={fetchMarketplace}
      >
        Refresh list
      </Button>
    </div>
  );

  const containerMargin = isMobile ? "8px 12px 20px" : "4px 20px 20px 20px";
  const filterColumnVisible = filtersOpen || !isMobile;

  const renderTimeline = (offer) => {
    const history = Array.isArray(offer.status_history)
      ? [...offer.status_history].reverse()
      : [];

    if (!history.length) return null;

    const items = history.map((entry, index) => ({
      color: HISTORY_COLOR[entry.actor] || "gray",
      children: (
        <div key={`history-${offer.id}-${index}`}>
          <div style={{ fontWeight: 600 }}>
            {HISTORY_LABELS[entry.action] || entry.action}
          </div>
          {entry.message && <div style={{ marginTop: 4 }}>{entry.message}</div>}
          {entry.disbursement_date && (
            <div style={{ marginTop: 4, fontSize: 11 }}>
              Disbursement date: {formatDate(entry.disbursement_date)}
            </div>
          )}
          <div
            style={{ marginTop: 4, fontSize: 11, color: "rgba(10,61,98,0.6)" }}
          >
            {(entry.actor === "borrower" && "Borrower") ||
              (entry.actor === "lender" && "You") ||
              "System"}
            {" · "}
            {formatDateTime(entry.timestamp)}
          </div>
        </div>
      ),
    }));

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: PRIMARY_COLOR }}>
          Activity
        </div>
        <Timeline style={{ marginTop: 8 }} items={items} />
      </div>
    );
  };

  const renderLoanCard = (loan) => {
    const statusDisplay = STATUS_COPY[loan.status] || {
      label: loan.status,
      color: "default",
    };
    const showOfferSection = loan.my_offer;
    const myOffer = loan.my_offer;
    const documentsFromBorrower =
      myOffer?.documents?.filter((doc) => doc.role === "borrower") || [];

    return (
      <div
        key={loan.id}
        style={{
          position: "relative",
          overflow: "hidden",
          background: loan.highlight
            ? HIGHLIGHT_CARD_BACKGROUND
            : GLASS_CARD_BACKGROUND,
          borderRadius: 18,
          padding: 22,
          marginBottom: 20,
          boxShadow: loan.highlight
            ? "0 16px 36px rgba(39,174,96,0.18)"
            : "0 18px 36px rgba(10,61,98,0.12)",
          border:
            loan.rejected_by_me && !loan.my_offer
              ? "1px solid rgba(231, 76, 60, 0.4)"
              : "1px solid rgba(10,61,98,0.06)",
          backdropFilter: "blur(8px)",
          color: PRIMARY_COLOR,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: loan.highlight
              ? "linear-gradient(90deg, rgba(39,174,96,0.5), rgba(39,174,96,0))"
              : "linear-gradient(90deg, rgba(10,61,98,0.35), rgba(10,61,98,0))",
            opacity: 0.18,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 4,
            width: "100%",
            background: loan.highlight
              ? "linear-gradient(90deg, rgba(39,174,96,0.95), rgba(26,188,156,0.6))"
              : "linear-gradient(90deg, rgba(10,61,98,0.9), rgba(10,61,98,0.4))",
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {loan.borrower_summary?.name || "Borrower"}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(10,61,98,0.6)",
                  marginTop: 4,
                }}
              >
                Created {formatDateTime(loan.created_at)}
              </div>
            </div>
            <Tag color={statusDisplay.color}>{statusDisplay.label}</Tag>
          </div>

          <div
            style={{
              display: "flex",
              gap: 18,
              flexWrap: "wrap",
              marginTop: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>Requested amount</div>
              <div style={{ fontWeight: 600 }}>
                {formatCurrency(loan.amount)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>Preferred rate</div>
              <div style={{ fontWeight: 600 }}>
                {formatPercent(loan.desired_rate_pa)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>Preferred tenor</div>
              <div style={{ fontWeight: 600 }}>
                {formatMonths(loan.desired_tenor_months)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>MyInfo last sync</div>
              <div style={{ fontWeight: 600 }}>
                {formatDateTime(loan.borrower_summary?.myinfo_last_sync)}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Button
              type="primary"
              onClick={() => openOffer(loan, loan.my_offer)}
              style={PRIMARY_BUTTON_STYLE}
            >
              {loan.my_offer ? "View offer" : "Make offer"}
            </Button>
            <Tooltip
              title={
                loan.rejected_by_me && !loan.my_offer
                  ? "You have already rejected this application."
                  : "Rejecting hides this loan from your marketplace view."
              }
            >
              <Button
                onClick={() => openRejection(loan)}
                disabled={loan.rejected_by_me && !loan.my_offer}
                style={{
                  borderColor: PRIMARY_COLOR,
                  color: PRIMARY_COLOR,
                }}
              >
                {loan.rejected_by_me && !loan.my_offer ? "Rejected" : "Reject"}
              </Button>
            </Tooltip>
          </div>

          {showOfferSection && (
            <div
              style={{
                position: "relative",
                overflow: "hidden",
                marginTop: 20,
                padding: 20,
                borderRadius: 16,
                background: GLASS_CARD_BACKGROUND,
                boxShadow: "0 16px 32px rgba(10,61,98,0.1)",
                border: "1px solid rgba(10,61,98,0.08)",
                color: PRIMARY_COLOR,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, rgba(10,61,98,0.35), rgba(10,61,98,0))",
                  opacity: 0.12,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: 3,
                  width: "100%",
                  background:
                    "linear-gradient(90deg, rgba(10,61,98,0.9), rgba(10,61,98,0.4))",
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>Your offer</div>
                  <Tag color="geekblue">
                    {OFFER_STATUS_COPY[myOffer.status] || myOffer.status}
                  </Tag>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    flexWrap: "wrap",
                    marginTop: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>
                      Offer amount
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {formatCurrency(myOffer.offer_data?.amount)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>
                      Interest rate
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {formatPercent(myOffer.offer_data?.rate_pa)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>Tenor</div>
                    <div style={{ fontWeight: 600 }}>
                      {formatMonths(
                        (myOffer.offer_data?.tenor_years || 0) * 12
                      )}
                    </div>
                  </div>
                  {myOffer.disbursement_date && (
                    <div>
                      <div style={{ fontSize: 11, opacity: 0.6 }}>
                        Disbursement date
                      </div>
                      <div style={{ fontWeight: 600 }}>
                        {formatDate(myOffer.disbursement_date)}
                      </div>
                    </div>
                  )}
                </div>

                {documentsFromBorrower.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      Borrower documents
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        marginTop: 6,
                      }}
                    >
                      {documentsFromBorrower.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.file_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          <DescriptionIcon fontSize="small" />
                          <span>{doc.file_name || "Download"}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    icon={<UploadFileIcon fontSize="small" />}
                    onClick={() => triggerFileDialog(myOffer.id)}
                    loading={documentBusy === myOffer.id}
                    style={PRIMARY_BUTTON_STYLE}
                  >
                    {documentBusy === myOffer.id
                      ? "Uploading..."
                      : "Upload documents"}
                  </Button>
                  {myOffer.status === "pending_lender_confirmation" ||
                  myOffer.status === "revision_requested" ? (
                    <Button
                      type="primary"
                      onClick={() => openOffer(loan, myOffer)}
                      style={PRIMARY_BUTTON_STYLE}
                    >
                      Review documents
                    </Button>
                  ) : null}
                </div>
                <input
                  type="file"
                  style={{ display: "none" }}
                  multiple
                  accept="application/pdf,image/*"
                  ref={(el) => (fileInputRef.current[myOffer.id] = el)}
                  onChange={(event) => handleFileChange(myOffer, event)}
                />

                {renderTimeline(myOffer)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderOfferModalContent = () => {
    if (!offerModal.offer) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Offer quantum</div>
            <InputNumber
              style={{ width: "100%", marginTop: 6 }}
              min={1000}
              max={200000}
              value={offerForm.amount}
              formatter={(value) =>
                `S$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/S\$\s?|(,*)/g, "")}
              onChange={(value) =>
                setOfferForm((prev) => ({ ...prev, amount: value }))
              }
            />
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Interest (% p.a.)</div>
            <InputNumber
              style={{ width: "100%", marginTop: 6 }}
              min={0}
              max={100}
              value={offerForm.rate_pa}
              onChange={(value) =>
                setOfferForm((prev) => ({ ...prev, rate_pa: value }))
              }
            />
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Tenor (years)</div>
            <InputNumber
              style={{ width: "100%", marginTop: 6 }}
              min={0.25}
              max={10}
              step={0.25}
              value={offerForm.tenor_years}
              onChange={(value) =>
                setOfferForm((prev) => ({ ...prev, tenor_years: value }))
              }
            />
          </div>
          <Input.TextArea
            rows={3}
            placeholder="Optional message to the borrower"
            value={reviewMessage}
            onChange={(event) => setReviewMessage(event.target.value)}
          />
        </div>
      );
    }

    const draftYearState = expandedDraftYears[currentDraftKey] || {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="sl-personal-draft-fields" style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Disbursement date</div>
            <input
              type="date"
              value={disbursementDate || ""}
              onChange={(event) => setDisbursementDate(event.target.value)}
              style={{
                width: "100%",
                marginTop: 6,
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #d0d7de",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, opacity: 0.6 }}>
              Notes for borrower (optional)
            </div>
            <Input.TextArea
              rows={3}
              value={reviewMessage}
              onChange={(event) => setReviewMessage(event.target.value)}
            />
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Repayment schedule
          </div>
          <div
            className="sl-lender-table-scroll sl-personal-draft-schedule"
            style={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr 1.2fr",
                background: "rgba(10,61,98,0.08)",
                fontWeight: 600,
                fontSize: 12,
                padding: "8px 12px",
                gap: 8,
              }}
            >
              <div>Due date</div>
              <div>Total</div>
              <div>Principal</div>
              <div>Interest</div>
              <div>Balance</div>
              <div>Notes</div>
            </div>
            {groupedDraftSchedules.map(({ year, label, items }) => {
              const isOpen = draftYearState[year] !== false;
              const yearTotal = items.reduce(
                (sum, { row }) => sum + Number(row.amount_due || 0),
                0
              );
              return (
                <div key={`draft-group-${year}`}>
                  <div
                    onClick={() => toggleDraftYear(year)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 12px",
                      background: "rgba(10,61,98,0.06)",
                      borderTop: "1px solid rgba(10,61,98,0.08)",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{label}</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: 12,
                        color: "rgba(10,61,98,0.7)",
                      }}
                    >
                      <span>{items.length} entries</span>
                      {yearTotal ? (
                        <span>{formatCurrency(yearTotal)}</span>
                      ) : null}
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 18,
                          lineHeight: 1,
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </div>
                  </div>
                  {isOpen &&
                    items.map(({ row, index }) => (
                      <div
                        key={`schedule-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr 1.2fr",
                          padding: "8px 12px",
                          gap: 8,
                          borderTop: "1px solid rgba(0,0,0,0.05)",
                          alignItems: "center",
                          background:
                            year === YEAR_UNDATED
                              ? "rgba(255,255,255,0.65)"
                              : "transparent",
                        }}
                      >
                        <input
                          type="date"
                          value={row.due_date}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "due_date",
                              event.target.value
                            )
                          }
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <InputNumber
                          min={0}
                          value={row.amount_due}
                          onChange={(value) =>
                            updateScheduleRow(index, "amount_due", value)
                          }
                          style={{ width: "100%" }}
                        />
                        <InputNumber
                          min={0}
                          value={row.principal_amount}
                          onChange={(value) =>
                            updateScheduleRow(index, "principal_amount", value)
                          }
                          style={{ width: "100%" }}
                        />
                        <InputNumber
                          min={0}
                          value={row.interest_amount}
                          onChange={(value) =>
                            updateScheduleRow(index, "interest_amount", value)
                          }
                          style={{ width: "100%" }}
                        />
                        <InputNumber
                          min={0}
                          value={row.loan_balance}
                          onChange={(value) =>
                            updateScheduleRow(index, "loan_balance", value)
                          }
                          style={{ width: "100%" }}
                        />
                        <div style={{ display: "flex", gap: 6 }}>
                          <Input
                            value={row.notes}
                            onChange={(event) =>
                              updateScheduleRow(
                                index,
                                "notes",
                                event.target.value
                              )
                            }
                          />
                          <Button
                            danger
                            type="link"
                            onClick={() => removeScheduleRow(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
            {groupedDraftSchedules.length === 0 && (
              <div
                style={{
                  padding: "16px 12px",
                  fontSize: 12,
                  color: "rgba(10,61,98,0.6)",
                }}
              >
                No repayment rows yet. Add your first instalment using the
                button below.
              </div>
            )}
          </div>
          <Button
            style={{ marginTop: 10, ...PRIMARY_BUTTON_STYLE }}
            onClick={addScheduleRow}
          >
            Add repayment row
          </Button>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button
            type="primary"
            loading={reviewBusy}
            onClick={() => handleReviewDecision(offerModal.offer, "confirm")}
            style={PRIMARY_BUTTON_STYLE}
          >
            Confirm documents
          </Button>
          <Button
            danger
            ghost
            loading={reviewBusy}
            onClick={() =>
              handleReviewDecision(offerModal.offer, "request_revision")
            }
            style={{ borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }}
          >
            Request revision
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Box
      m={containerMargin}
      style={{ color: PRIMARY_COLOR, overflowX: "hidden" }}
      className="glassStyle sl-personal-marketplace"
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: isMobile ? 20 : 28,
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        <div
          style={{
            flex: "1 1 620px",
            minWidth: isMobile ? "100%" : 420,
          }}
        >
          <div
            className="sl-personal-marketplace-head"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 22,
            }}
          >
            <div>
              <div
                style={{ fontSize: 24, fontWeight: 700, color: PRIMARY_COLOR }}
              >
                Personal loan marketplace
              </div>
              <div style={{ fontSize: 13, color: "rgba(10,61,98,0.6)" }}>
                Review borrower applications and manage your offers.
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              {isMobile && (
                <Button
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  style={{
                    ...PRIMARY_BUTTON_STYLE,
                    background: "rgba(10,61,98,0.08)",
                    color: PRIMARY_COLOR,
                    borderColor: "rgba(10,61,98,0.24)",
                  }}
                >
                  {filtersOpen ? "Hide filters" : "Show filters"}
                </Button>
              )}
              <Button onClick={fetchMarketplace} style={PRIMARY_BUTTON_STYLE}>
                Refresh
              </Button>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 80,
              }}
            >
              <CircularProgress size={32} />
            </div>
          ) : filteredLoans.length ? (
            filteredLoans.map(renderLoanCard)
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "rgba(10, 61, 98, 0.55)",
                fontSize: "13px",
                background: "rgba(255,255,255,0.6)",
                borderRadius: 16,
                boxShadow: "0 12px 30px rgba(10,61,98,0.08)",
              }}
            >
              No personal loan requests match your current filters.
            </div>
          )}
        </div>

        {filterColumnVisible && (
          <div
            style={{
              flex: isMobile ? "1 1 100%" : "0 0 300px",
              width: "100%",
              maxWidth: isMobile ? "100%" : 320,
              order: isMobile ? -1 : 0,
            }}
          >
            {filtersPanel}
          </div>
        )}
      </div>

      {amendmentModal}
      <Modal
        open={offerModal.open}
        title={null}
        onCancel={closeOfferModal}
        footer={null}
        width={offerModal.offer ? 900 : 520}
        bodyStyle={MODAL_BODY_STYLE}
        maskStyle={MODAL_MASK_STYLE}
        style={{ top: 60 }}
        destroyOnClose
        closable={false}
        rootClassName="glass-modal"
        className="glass-modal"
        styles={{
          padding: "0px 0px",
          content: {
            background: "transparent",
            padding: 0,
            boxShadow: "none",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div style={GLASS_MODAL_PANEL_STYLE}>
          <button
            type="button"
            className="glass-modal__close"
            onClick={closeOfferModal}
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
          <div
            style={{
              padding: offerModal.offer ? 28 : 24,
              display: "flex",
              flexDirection: "column",
              gap: 18,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 20 }}>
              {offerModal.offer
                ? "Manage personal loan offer"
                : "Submit personal loan offer"}
            </div>
            <div
              style={{
                height: 1,
                background: "rgba(10,61,98,0.12)",
                borderRadius: 2,
              }}
            />
            {offerModal.offer && renderAmendOfferButton({ loanType: "personal", offer: offerModal.offer, onUpdated: () => { closeOfferModal(); fetchMarketplace(); } })}
            {renderOfferModalContent()}
            {!offerModal.offer && (
              <div className="glass-modal__actions">
                <Button
                  onClick={closeOfferModal}
                  style={{
                    borderColor: PRIMARY_COLOR,
                    color: PRIMARY_COLOR,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={offerBusy}
                  onClick={submitOffer}
                  style={PRIMARY_BUTTON_STYLE}
                >
                  Submit offer
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={rejectionModal.open}
        title={null}
        onCancel={() => setRejectionModal({ open: false, loan: null })}
        footer={null}
        bodyStyle={MODAL_BODY_STYLE}
        maskStyle={MODAL_MASK_STYLE}
        style={{ top: 80 }}
        destroyOnClose
        closable={false}
        rootClassName="glass-modal"
        className="glass-modal"
        styles={{
          content: {
            background: "transparent",
            padding: 0,
            boxShadow: "none",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div style={GLASS_MODAL_PANEL_STYLE}>
          <button
            type="button"
            className="glass-modal__close"
            onClick={() => setRejectionModal({ open: false, loan: null })}
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
          <div
            style={{
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              Reject application
            </div>
            <div
              style={{
                height: 1,
                background: "rgba(10,61,98,0.12)",
                borderRadius: 2,
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>Reason</div>
                <Select
                  allowClear
                  style={{ width: "100%", marginTop: 6 }}
                  value={rejectionForm.reason}
                  onChange={(value) =>
                    setRejectionForm((prev) => ({ ...prev, reason: value }))
                  }
                  options={rejectionOptions.map((option) => ({
                    value: option,
                    label: option.replace(/_/g, " "),
                  }))}
                  placeholder="Select a reason"
                />
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  Notes (optional)
                </div>
                <Input.TextArea
                  rows={3}
                  value={rejectionForm.notes}
                  onChange={(event) =>
                    setRejectionForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="glass-modal__actions">
              <Button
                onClick={() => setRejectionModal({ open: false, loan: null })}
                style={{
                  borderColor: PRIMARY_COLOR,
                  color: PRIMARY_COLOR,
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={rejectionBusy}
                onClick={submitRejection}
                style={PRIMARY_BUTTON_STYLE}
              >
                Submit rejection
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Box>
  );
};

export default PersonalLoanMarketplace;
